import { useEffect, useId, useRef, useState } from 'react';
import styles from './Bookshelf.module.css';

/* One word of the sentence: the current choice, underlined, opening a short
   list of the others. A listbox rather than a menu because each is a single
   choice among values, which is what a screen reader should announce. */
function Choice({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listRef = useRef(null);
  const id = useId();
  const current = options.find(o => o.id === value) || options[0];

  useEffect(() => {
    if (!open) return undefined;
    const onDown = e => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    listRef.current?.querySelector('[aria-selected="true"]')?.focus();
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  const pick = option => {
    onChange(option.id);
    setOpen(false);
    rootRef.current?.querySelector('button')?.focus();
  };

  const onListKey = e => {
    const items = [...listRef.current.querySelectorAll('[role="option"]')];
    const at = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      items[(at + step + items.length) % items.length]?.focus();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      rootRef.current?.querySelector('button')?.focus();
    }
  };

  return (
    <span className={styles.choice} ref={rootRef}>
      <button
        type="button"
        className={styles.choiceButton}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={id}
        aria-label={`${label}: ${current.label}`}
        onClick={() => setOpen(o => !o)}
      >
        {current.label}
      </button>
      {open && (
        <ul
          id={id}
          ref={listRef}
          role="listbox"
          aria-label={label}
          className={styles.choiceList}
          onKeyDown={onListKey}
        >
          {options.map(option => (
            <li
              key={option.id}
              role="option"
              tabIndex={-1}
              aria-selected={option.id === current.id}
              className={styles.choiceOption}
              onClick={() => pick(option)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  pick(option);
                }
              }}
            >
              <span>{option.label}</span>
              {option.count != null && <span className={styles.choiceCount}>{option.count}</span>}
            </li>
          ))}
        </ul>
      )}
    </span>
  );
}

/* "Showing all books from every genre, in shelf order." Each underlined phrase
   is a control. It sits above the pile and rides it (--shelfShift, written by
   the scene), so it scrolls away with the books. */
export function ShelfSentence({ view, options, onChange, shown, total, headRef }) {
  return (
    <div className={styles.sentence} ref={headRef}>
      {/* A div, not a p: each choice opens a list, and a list cannot sit in a p. */}
      <div className={styles.sentenceText}>
        Showing{' '}
        <Choice
          label="Which books"
          value={view.status}
          options={options.status}
          onChange={status => onChange({ status })}
        />{' '}
        from{' '}
        <Choice
          label="Genre"
          value={view.genre}
          options={options.genre}
          onChange={genre => onChange({ genre })}
        />
        ,{' '}
        <Choice
          label="Order"
          value={view.sort}
          options={options.sort}
          onChange={sort => onChange({ sort })}
        />
        .
      </div>
      <p className={styles.sentenceTally} aria-live="polite">
        {shown === total ? `${total} books` : `${shown} of ${total} books`}
      </p>
    </div>
  );
}
